import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Stack,
  Autocomplete,
  createFilterOptions,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Drawer,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import Clear from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import testService from '../../services/testService';
import { ASSIGNMENTS, ATTEMPTS, TESTS, TESTS_WORKSHEET } from '../../config/Config';
import {
  fetchUnifiedTeacherStudents,
  formatTeacherStudentOptionLabel,
  getManagedStudentRecordId,
  getPlatformStudentPk,
  isManagedRosterStudent,
} from '../../utils/teacherStudentRoster';
import AssignTestsDialog from '../../components/tests/AssignTestsDialog';
import ViewTestQuestionsDialog, { VIEW_QUESTION_COLUMNS as viewQuestionColumns } from '../../components/tests/ViewTestQuestionsDialog';
import AutoTestDialog from '../../components/tests/AutoTestDialog';
import ManualTestDialog from '../../components/tests/ManualTestDialog';
import TestResultsPage from '../tests/ResultsPage';
import PdfAnnotationViewer from '../../components/tests/PdfAnnotationViewer';
import TestQuestionReviewDialog from '../../components/tests/TestQuestionReviewDialog';
import uploadService from '../../services/uploadService';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SUBJECTS, BOARDS_EXAMS, ACADEMIC_YEARS, SUBJECT_TOPICS, TESTS_GENERATE, TEST_TYPES } from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';
import SummaryCard from '../../components/common/SummaryCard';
import {
  teacherFilterClearButtonSx,
  teacherFilterFieldSx,
  teacherFilterHeaderSx,
  teacherFilterPanelSx,
  teacherFilterTitleSx,
  teacherFilterTopStackSx,
} from '../../styles/teacherFilterStyles';

const studentFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) => `${option.email || ''} ${option.name || ''} ${option.display_name || ''}`,
});

const TAB_KEYS = ['to_be_completed', 'completed', 'ready_for_review', 'grading_complete'];

/** An attempt counts as completed when submitted, graded, timed out, or ended. */
const isCompletedAttempt = (attempt) =>
  !!attempt && (
    attempt.submitted ||
    ['SUBMITTED', 'GRADED', 'TIMED_OUT'].includes(attempt.status) ||
    !!attempt.end_time
  );

/** Store attempt under key, never letting an in-progress/abandoned attempt shadow a
 *  completed one. The API returns attempts newest-first, so among equals the first
 *  stored (most recent) wins. */
const setPreferredAttempt = (map, key, attempt) => {
  const existing = map.get(key);
  if (!existing || (isCompletedAttempt(attempt) && !isCompletedAttempt(existing))) {
    map.set(key, attempt);
  }
};

/** Format a YYYY-MM-DD date for display (e.g. "12 Jul 2026"); returns '—' when empty. */
const formatDueDate = (value) => {
  if (!value) return '—';
  const dt = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return isNaN(dt.getTime())
    ? String(value)
    : dt.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Student worksheet (invited) or teacher worksheet upload (self-managed roster) both count toward review. */
const hasWorksheetUploadedAssignment = (a) =>
  (a.uploadCount || 0) > 0 || (a.teacherUploadCount || 0) > 0;

const getAssignmentTabCategory = (a) => {
  if (a.status === 'Pending') return 'to_be_completed';
  if (a.status === 'Completed' && a.gradingComplete) return 'grading_complete';
  if (a.status === 'Completed' && hasWorksheetUploadedAssignment(a)) return 'ready_for_review';
  return 'completed';
};

function StudentAssignmentsPage() {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Check if user is a parent
  const isParent = user?.isParent || user?.is_parent || (user?.role && user?.role.toString().toLowerCase() === 'parent');

  /* ---------- State ---------- */
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const tabFromUrl = searchParams.get('tab');
  const initialTabIndex = TAB_KEYS.includes(tabFromUrl) ? TAB_KEYS.indexOf(tabFromUrl) : 0;
  const [activeTab, setActiveTab] = useState(initialTabIndex); // 0: to_be_completed, 1: completed, 2: ready_for_review, 3: grading_complete
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Keep tab in sync with URL (e.g. when navigating from dashboard "Pending Grading" card)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (TAB_KEYS.includes(tab)) setActiveTab(TAB_KEYS.indexOf(tab));
  }, [searchParams]);

  const [assignments, setAssignments] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const fetchTokenRef = useRef(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignMenuAnchor, setAssignMenuAnchor] = useState(null);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [newTestDialogOpen, setNewTestDialogOpen] = useState(false);
  
  // Question review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [generatedTestId, setGeneratedTestId] = useState(null);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [pendingTestData, setPendingTestData] = useState(null);
  const [pendingStudent, setPendingStudent] = useState(null);
  
  // Data for test creation dialogs
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [years, setYears] = useState([]);
  const [topics, setTopics] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showMsg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const clearFilters = () => {
    setSearch('');
    setSelectedStudent(null);
    fetchAllAssignments();
  };

  // dialogs for preview & results
  const [questionsDialog, setQuestionsDialog] = useState({ open: false, questions: [], test: null });
  const [resultDialog, setResultDialog] = useState({ open: false, attemptId: null });
  const [teacherDialog, setTeacherDialog] = useState({ open:false, testId:null, studentId:null });
  const [annotateDialog, setAnnotateDialog] = useState({ open: false, uploadId: null, filename: null });

  /* ---------- Helpers ---------- */
  // Extract the primary-key used in TestAssignment.student_id consistently
  const getStudentPk = (s) => getPlatformStudentPk(s);

  /** Platform student user id for assignment/attempt APIs; null for roster-only. */
  const getStudentId = (studentObj) => getPlatformStudentPk(studentObj);

  const buildAssignmentPayloadFor = (studentObj, testId, extra = {}) => {
    const msId = getManagedStudentRecordId(studentObj);
    if (msId) {
      return { test: testId, teacher_managed_student_ids: [msId], ...extra };
    }
    const sid = getPlatformStudentPk(studentObj);
    if (!sid) {
      throw new Error('Could not resolve student for assignment');
    }
    return { test: testId, students: [sid], ...extra };
  };

  /** Deep link: /teacher/assignments?student=<userId> pre-selects that student in the filter. */
  const appliedStudentFromUrlRef = useRef(null);

  const fetchStudents = async () => {
    try {
      setStudentsLoading(true);
      const list = await fetchUnifiedTeacherStudents(api);
      setStudents(list);
    } catch (err) {
      showMsg(err.message, 'error');
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchAllAssignments = async () => {
    const token = ++fetchTokenRef.current;
    setAssignments([]);
    try {
      setAssignLoading(true);
      const res = await api.get(`${ASSIGNMENTS}?page_size=1000`);
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];
      const baseRows = list.map(a => ({
        id: a.id,
        test: a.test,
        student: a.student || a.student_id,
        teacher_managed_student_id: a.teacher_managed_student_id ?? null,
        assignment_origin_label: a.assignment_origin_label || (a.teacher_managed_student_id ? 'Teacher-managed' : 'Linked student'),
        student_first_name: a.student_first_name || a.student?.first_name || '',
        student_name: a.student_first_name || a.student?.first_name || '',  
        student_email: a.student_email || a.student?.email || '',
        title: a.test_title,
        subject: a.subject_name,
        topics_list: a.topics_list || [a.topic_name].filter(Boolean),
        topic: a.topics_list ? (a.topics_list || []).join(', ') : a.topic_name,
        year: a.academic_year,
        student_upload_count: a.student_upload_count,
        teacher_upload_count: a.teacher_upload_count,
        roster_display_name: a.roster_display_name || '',
        target_date: a.target_date || null,
          has_completed_attempt: a.has_completed_attempt === true,
          completed_attempt_id: a.completed_attempt_id ?? null,
          has_grading_complete: a.has_grading_complete === true,
      }));
      // Fetch attempts for each unique platform student and each roster row
      const uniqueStudentIds = [...new Set(baseRows.map(r => {
        const s = r.student;
        return typeof s === 'object' ? s?.id : s;
      }).filter(Boolean))];
      const uniqueTmIds = [...new Set(baseRows.map(r => r.teacher_managed_student_id).filter(Boolean))];

      const fetchAttemptsForStudent = async (studentId, retryCount = 0) => {
        try {
          const attemptsRes = await api.get(`${ATTEMPTS}?student_id=${studentId}&page_size=1000`);
          return Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data.results || []);
        } catch (err) {
          if (err.response?.status === 429 && retryCount < 3) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            return fetchAttemptsForStudent(studentId, retryCount + 1);
          }
          try {
            const attemptsRes = await api.get(`${ATTEMPTS}?student=${studentId}&page_size=1000`);
            return Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data.results || []);
          } catch {}
          return [];
        }
      };

      const fetchAttemptsForManaged = async (tmId, retryCount = 0) => {
        try {
          const attemptsRes = await api.get(`${ATTEMPTS}?teacher_managed_student_id=${tmId}&page_size=1000`);
          return Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data.results || []);
        } catch (err) {
          if (err.response?.status === 429 && retryCount < 3) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            return fetchAttemptsForManaged(tmId, retryCount + 1);
          }
          return [];
        }
      };

      const allAttemptsArrays = await Promise.all([
        ...uniqueStudentIds.map(id => fetchAttemptsForStudent(id)),
        ...uniqueTmIds.map(id => fetchAttemptsForManaged(id)),
      ]);
      const allAttempts = allAttemptsArrays.flat();

      // Skip upload fetching for now to avoid permission issues
      let allStudentUploads = [];
      let allTeacherUploads = [];

      // Key by student_id + test_id or roster + test_id
      const attemptsMap = new Map();
      allAttempts.forEach(attempt => {
        const sid = attempt.student_id ?? (typeof attempt.student === 'object' ? attempt.student?.id : attempt.student);
        const tm = attempt.teacher_managed_student_id;
        if (sid != null) {
          setPreferredAttempt(attemptsMap, `s:${sid}:${attempt.test}`, attempt);
        }
        if (tm != null) {
          setPreferredAttempt(attemptsMap, `tm:${tm}:${attempt.test}`, attempt);
        }
      });

      const studentUploadsMap = new Map();
      allStudentUploads.forEach(upload => {
        if (!studentUploadsMap.has(upload.test_id)) {
          studentUploadsMap.set(upload.test_id, []);
        }
        studentUploadsMap.get(upload.test_id).push(upload);
      });

      const teacherUploadsMap = new Map();
      allTeacherUploads.forEach(upload => {
        if (!teacherUploadsMap.has(upload.test_id)) {
          teacherUploadsMap.set(upload.test_id, []);
        }
        teacherUploadsMap.get(upload.test_id).push(upload);
      });

      // Enrich assignments with data from the maps
      const enriched = baseRows.map(row => {
        const rowStudentId = typeof row.student === 'object' ? row.student?.id : row.student;
        const rowTmId = row.teacher_managed_student_id;
        // Try the managed-roster key first, then fall back to the platform-student key:
        // a roster student who onboarded takes tests under their platform account.
        const attempt =
          (rowTmId != null ? attemptsMap.get(`tm:${rowTmId}:${row.test}`) : undefined) ??
          (rowStudentId != null ? attemptsMap.get(`s:${rowStudentId}:${row.test}`) : undefined);
        // Backend annotation is authoritative; the client-side attempt match adds attempt details.
        const completed = row.has_completed_attempt === true || isCompletedAttempt(attempt);
        
        // Use upload counts from backend if available, otherwise fall back to local maps
        const stuUploads = row.student_upload_count !== undefined ? row.student_upload_count : (studentUploadsMap.get(row.test)?.length || 0);
        const teacherUploads = row.teacher_upload_count !== undefined ? row.teacher_upload_count : (teacherUploadsMap.get(row.test)?.length || 0);

        // Backend annotation is authoritative; the client-side note check is a fallback.
        const gradingComplete = row.has_grading_complete === true || !!(attempt?.teacher_notes_entries?.length &&
          attempt.teacher_notes_entries.some(n => n.is_marked === true || n.is_marked === 'Yes' || String(n.is_marked).toLowerCase() === 'yes'));
        return {
          ...row,
          status: completed ? 'Completed' : 'Pending',
          attemptId: attempt?.id || row.completed_attempt_id || null,
          uploadCount: stuUploads,
          teacherUploadCount: teacherUploads,
          gradingComplete,
        };
      });
      if (token === fetchTokenRef.current) setAssignments(enriched);
    } catch (err) { showMsg(err.message,'error'); } finally { if(token===fetchTokenRef.current) setAssignLoading(false); }
  };

  const fetchAssignments = async studentObj => {
    const token = ++fetchTokenRef.current;
    // reset previous list while loading new student
    setAssignments([]);
    if (!studentObj) return;
    
    const msId = getManagedStudentRecordId(studentObj);
    const platformId = getPlatformStudentPk(studentObj);
    if (!msId && !platformId) {
      showMsg('Could not determine student or roster entry', 'error');
      setAssignments([]);
      return;
    }

    try {
      setAssignLoading(true);
      const assignUrl = msId
        ? `${ASSIGNMENTS}?teacher_managed_student_id=${msId}&page_size=1000`
        : `${ASSIGNMENTS}?student=${platformId}&page_size=1000`;
      const res = await api.get(assignUrl);
      const list = Array.isArray(res.data) ? res.data : res.data.results || [];

      // Base rows
      const baseRows = list.map(a => ({
          id: a.id,
          test: a.test,
          student: a.student || a.student_id, // store student user id
          teacher_managed_student_id: a.teacher_managed_student_id ?? null,
          assignment_origin_label: a.assignment_origin_label || (a.teacher_managed_student_id ? 'Teacher-managed' : 'Linked student'),
          student_first_name: a.student_first_name || a.student?.first_name || '',
          student_name: a.student_first_name || a.student?.first_name || '',  
          student_email: a.student_email || a.student?.email || '',
          title: a.test_title,
          subject: a.subject_name,
          topics_list: a.topics_list || [a.topic_name].filter(Boolean),
          topic: a.topics_list ? (a.topics_list || []).join(', ') : a.topic_name,
          year: a.academic_year,
          student_upload_count: a.student_upload_count,
          teacher_upload_count: a.teacher_upload_count,
          roster_display_name: a.roster_display_name || '',
          target_date: a.target_date || null,
          has_completed_attempt: a.has_completed_attempt === true,
          completed_attempt_id: a.completed_attempt_id ?? null,
          has_grading_complete: a.has_grading_complete === true,
      }));

      // Attempts: managed rows use teacher_managed_student_id, platform rows use student_id.
      // When filtering by platform student, the API can return merged teacher-managed assignments;
      // we must load attempts for those managed ids too (otherwise status stays "Pending").
      const uniqueTmIdsFromRows = [...new Set(baseRows.map((r) => r.teacher_managed_student_id).filter(Boolean))];

      const fetchAttemptsForStudentSingle = async (studentId, retryCount = 0) => {
        try {
          const attemptsRes = await api.get(`${ATTEMPTS}?student_id=${studentId}&page_size=1000`);
          return Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data.results || []);
        } catch (err) {
          if (err.response?.status === 429 && retryCount < 3) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            return fetchAttemptsForStudentSingle(studentId, retryCount + 1);
          }
          try {
            const attemptsRes = await api.get(`${ATTEMPTS}?student=${studentId}&page_size=1000`);
            return Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data.results || []);
          } catch {
            return [];
          }
        }
      };

      const fetchAttemptsForManagedSingle = async (tmId, retryCount = 0) => {
        try {
          const attemptsRes = await api.get(`${ATTEMPTS}?teacher_managed_student_id=${tmId}&page_size=1000`);
          return Array.isArray(attemptsRes.data) ? attemptsRes.data : (attemptsRes.data.results || []);
        } catch (err) {
          if (err.response?.status === 429 && retryCount < 3) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            return fetchAttemptsForManagedSingle(tmId, retryCount + 1);
          }
          return [];
        }
      };

      let allAttempts = [];
      if (msId) {
        // Roster student: also load platform attempts when they have onboarded,
        // so tests taken under their own account still show as completed.
        const parts = await Promise.all([
          fetchAttemptsForManagedSingle(msId),
          ...(platformId ? [fetchAttemptsForStudentSingle(platformId)] : []),
        ]);
        allAttempts = parts.flat();
      } else {
        const parts = await Promise.all([
          fetchAttemptsForStudentSingle(platformId),
          ...uniqueTmIdsFromRows.map((id) => fetchAttemptsForManagedSingle(id)),
        ]);
        allAttempts = parts.flat();
      }

      // Skip upload fetching for now to avoid permission issues
      // Upload counts can be fetched when needed (e.g., when viewing individual assignments)
      let allStudentUploads = [];
      let allTeacherUploads = [];

      // Create lookup maps for faster matching
      const attemptsMap = new Map();
      allAttempts.forEach(attempt => {
        const sid = attempt.student_id ?? (typeof attempt.student === 'object' ? attempt.student?.id : attempt.student);
        const tm = attempt.teacher_managed_student_id;
        if (sid != null) {
          setPreferredAttempt(attemptsMap, `s:${sid}:${attempt.test}`, attempt);
        }
        if (tm != null) {
          setPreferredAttempt(attemptsMap, `tm:${tm}:${attempt.test}`, attempt);
        }
      });

      const studentUploadsMap = new Map();
      allStudentUploads.forEach(upload => {
        if (!studentUploadsMap.has(upload.test_id)) {
          studentUploadsMap.set(upload.test_id, []);
        }
        studentUploadsMap.get(upload.test_id).push(upload);
      });

      const teacherUploadsMap = new Map();
      allTeacherUploads.forEach(upload => {
        if (!teacherUploadsMap.has(upload.test_id)) {
          teacherUploadsMap.set(upload.test_id, []);
        }
        teacherUploadsMap.get(upload.test_id).push(upload);
      });

      // Enrich assignments with data from the maps
      const enriched = baseRows.map(row => {
        const rowStudentId = typeof row.student === 'object' ? row.student?.id : row.student;
        const rowTmId = row.teacher_managed_student_id;
        // Try the managed-roster key first, then fall back to the platform-student key:
        // a roster student who onboarded takes tests under their platform account.
        const attempt =
          (rowTmId != null ? attemptsMap.get(`tm:${rowTmId}:${row.test}`) : undefined) ??
          (rowStudentId != null ? attemptsMap.get(`s:${rowStudentId}:${row.test}`) : undefined);
        // Backend annotation is authoritative; the client-side attempt match adds attempt details.
        const completed = row.has_completed_attempt === true || isCompletedAttempt(attempt);
        
        // Use upload counts from backend if available, otherwise fall back to local maps
        const stuUploads = row.student_upload_count !== undefined ? row.student_upload_count : (studentUploadsMap.get(row.test)?.length || 0);
        const teacherUploads = row.teacher_upload_count !== undefined ? row.teacher_upload_count : (teacherUploadsMap.get(row.test)?.length || 0);

        // Backend annotation is authoritative; the client-side note check is a fallback.
        const gradingComplete = row.has_grading_complete === true || !!(attempt?.teacher_notes_entries?.length &&
          attempt.teacher_notes_entries.some(n => n.is_marked === true || n.is_marked === 'Yes' || String(n.is_marked).toLowerCase() === 'yes'));
        return {
          ...row,
          status: completed ? 'Completed' : 'Pending',
          attemptId: attempt?.id || row.completed_attempt_id || null,
          uploadCount: stuUploads,
          teacherUploadCount: teacherUploads,
          gradingComplete,
        };
      });
       if (token === fetchTokenRef.current) {
         setAssignments(enriched);
       }
    } catch (err) {
      showMsg(err.message, 'error');
    } finally {
       if (token === fetchTokenRef.current) setAssignLoading(false);
    }
  };

  useEffect(() => {
    const sid = searchParams.get('student');
    if (!sid) {
      appliedStudentFromUrlRef.current = null;
      return;
    }
    if (studentsLoading || !students.length) return;
    const key = String(sid);
    if (appliedStudentFromUrlRef.current === key) return;
    const match = students.find(
      (s) => String(s.id) === key || String(s.student) === key || String(getStudentId(s)) === key
    );
    if (match) {
      appliedStudentFromUrlRef.current = key;
      setSelectedStudent(match);
      fetchAssignments(match);
    }
    // fetchAssignments is stable enough for this one-shot URL hydration; avoid deps churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, students, studentsLoading]);

  useEffect(() => {
    fetchStudents();
    // Fetch data for test creation dialogs
    const fetchTestData = async () => {
      try {
        const [subjectsRes, boardsRes, yearsRes, topicsRes] = await Promise.all([
          api.get(`${SUBJECTS}?page_size=1000`),
          api.get(`${BOARDS_EXAMS}?page_size=1000&published_only=true`),
          api.get(`${ACADEMIC_YEARS}?page_size=1000&published_only=true`),
          api.get(`${SUBJECT_TOPICS}?page_size=10000`)
        ]);
        setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : subjectsRes.data.results || []);
        setBoards(filterPublished(Array.isArray(boardsRes.data) ? boardsRes.data : boardsRes.data.results || []));
        setYears(filterPublished(Array.isArray(yearsRes.data) ? yearsRes.data : yearsRes.data.results || []));
        setTopics(Array.isArray(topicsRes.data) ? topicsRes.data : topicsRes.data.results || []);
      } catch (error) {
        console.error('Error fetching test data:', error);
      }
    };
    fetchTestData();
  }, []);

  useEffect(() => {
    if (!selectedStudent || (selectedStudent && selectedStudent.id === 'all')) {
      fetchAllAssignments();
    }
  }, [selectedStudent]);

  // Refresh when the teacher returns to this tab so student submissions/uploads show up
  // without a manual reload (throttled to once per 30 seconds).
  const lastFocusRefreshRef = useRef(0);
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastFocusRefreshRef.current < 30000) return;
      lastFocusRefreshRef.current = Date.now();
      if (selectedStudent && selectedStudent.id !== 'all') fetchAssignments(selectedStudent);
      else fetchAllAssignments();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent]);

  /* ---------- Derived ---------- */
  const filteredStudents = useMemo(() => {
    const term = search.toLowerCase();
    return students.filter((s) =>
      `${s.email || ''} ${s.name || ''} ${s.display_name || ''}`.toLowerCase().includes(term)
    );
  }, [students, search]);

  const resolveAssignmentStudentLabel = useCallback(
    (row) => {
      if (!row) return '—';
      if (row.teacher_managed_student_id) {
        const rn = (row.roster_display_name || '').trim();
        if (rn) return rn;
        const m = students.find(
          (s) =>
            s.source_type === 'managed_student' &&
            String(s.managed_student_id) === String(row.teacher_managed_student_id)
        );
        if (m) return formatTeacherStudentOptionLabel(m) || '—';
      }
      const plain = (row.student_first_name || row.student_name || '').trim();
      if (plain) return plain;
      const studentId = row.student || row.student_id;
      if (studentId) {
        const match = students.find((s) => {
          const pk = getPlatformStudentPk(s);
          return pk != null && String(pk) === String(typeof studentId === 'object' ? studentId?.id : studentId);
        });
        if (match) return formatTeacherStudentOptionLabel(match) || '—';
      }
      return '—';
    },
    [students]
  );

  /* ---------- Columns ---------- */
  const studentColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'email', headerName: 'Email', flex: 1 },
  ];

  /* ---------- Tab categories: to_be_completed | completed | ready_for_review | grading_complete ---------- */
  const TAB_CONFIG = [
    {
      key: 'to_be_completed',
      label: 'To be completed',
      tooltip:
        'Pending test. For self-managed roster students, use Actions → Mark test as completed when the test was done offline.',
    },
    { key: 'completed', label: 'Test Taken', tooltip: 'Test taken, worksheet pending' },
    { key: 'ready_for_review', label: 'Ready for final review', tooltip: 'Test done, worksheet uploaded by student' },
    { key: 'grading_complete', label: 'Grading complete', tooltip: 'Teacher has marked grading complete' },
  ];

  const tabCounts = useMemo(() => {
    const counts = { to_be_completed: 0, completed: 0, ready_for_review: 0, grading_complete: 0 };
    assignments.forEach((a) => {
      counts[getAssignmentTabCategory(a)] += 1;
    });
    return counts;
  }, [assignments]);

  const filteredAssignments = useMemo(() => {
    const tabKey = TAB_CONFIG[activeTab]?.key;
    return assignments.filter((a) => getAssignmentTabCategory(a) === tabKey);
  }, [assignments, activeTab]);

  const renderTabLabel = (tab, idx) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, pr: 0.5 }}>
      <Box component="span" sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
        {tab.label}
      </Box>
      <Box
        component="span"
        aria-label={`${tabCounts[tab.key]} in ${tab.label}`}
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.65,
          borderRadius: 999,
          bgcolor: idx === activeTab ? 'primary.main' : 'action.hover',
          color: idx === activeTab ? 'primary.contrastText' : 'text.secondary',
          fontSize: '0.7rem',
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          boxShadow: idx === activeTab ? 1 : 'none',
          flexShrink: 0,
        }}
      >
        {tabCounts[tab.key]}
      </Box>
    </Box>
  );

  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [actionsMenuRow, setActionsMenuRow] = useState(null);
  const closeActionsMenu = () => { setActionsMenuAnchor(null); setActionsMenuRow(null); };

  // Edit due date (pending assignments only)
  const [dueDateDialog, setDueDateDialog] = useState({ open: false, row: null });
  const [newDueDate, setNewDueDate] = useState('');
  const [dueDateSaving, setDueDateSaving] = useState(false);

  const openDueDateDialog = (row) => {
    setNewDueDate(row?.target_date ? String(row.target_date).slice(0, 10) : '');
    setDueDateDialog({ open: true, row });
  };

  const handleSaveDueDate = async () => {
    const row = dueDateDialog.row;
    if (!row || !newDueDate) return;
    setDueDateSaving(true);
    try {
      await api.patch(`${ASSIGNMENTS}${row.id}/`, { target_date: newDueDate });
      showMsg('Due date updated');
      setDueDateDialog({ open: false, row: null });
      if (selectedStudent && selectedStudent.id !== 'all') fetchAssignments(selectedStudent);
      else fetchAllAssignments();
    } catch (e) {
      const msg = e?.response?.data?.target_date?.[0] || e?.response?.data?.detail || e?.message || 'Could not update due date';
      showMsg(msg, 'error');
    } finally {
      setDueDateSaving(false);
    }
  };

  const assignmentColumns = useMemo(() => [
    { field: 'actions', headerName: 'Actions', width: 140, sortable:false, renderCell:(params)=>(
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setActionsMenuAnchor(e.currentTarget);
            setActionsMenuRow(params.row);
          }}
          sx={{ textTransform: 'none' }}
        >
          Actions
        </Button>
      ) },
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'subject', headerName: 'Subject', width: 140 },
    {
      field: 'assignment_origin_label',
      headerName: 'Source',
      width: 150,
      renderCell: (params) => {
        const isManaged = params.value === 'Teacher-managed';
        return (
          <Chip
            size="small"
            label={params.value || 'Linked student'}
            color={isManaged ? 'warning' : 'info'}
            variant="outlined"
          />
        );
      },
    },
    { field: 'topic', headerName: 'Topics', width: 200 },
    { field: 'year', headerName: 'Year', width: 80 },
    {
      field: 'target_date',
      headerName: 'Due date',
      width: 110,
      renderCell: (params) => (
        <Tooltip title={params.value ? `Due ${formatDueDate(params.value)}` : 'No due date set'}>
          <span>{formatDueDate(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'student_name',
      headerName: isParent ? 'Child' : 'Student',
      width: 160,
      renderCell: (params) => {
        const row = params.row;
        const label = resolveAssignmentStudentLabel(row);
        const email = row.student_email || row.student?.email || '';
        return (
          <Tooltip title={email || label}>
            <span>{label}</span>
          </Tooltip>
        );
      }
    },  
    { field: 'status', headerName: 'Status', width: 170, renderCell: (p)=>{
        const currentTabKey = TAB_CONFIG[activeTab]?.key;
        const row = p.row;
        let displayStatus = p.value;
        if (currentTabKey === 'ready_for_review' && p.value === 'Completed') displayStatus = 'Worksheet uploaded';
        else if (currentTabKey === 'grading_complete' && row.gradingComplete) displayStatus = 'Grading complete';
        return (
          <Tooltip title={displayStatus} arrow>
            <span>
              <Chip label={displayStatus} color={p.value === 'Completed' || displayStatus === 'Grading complete' ? 'success' : 'warning'} size="small" />
            </span>
          </Tooltip>
        );
      } }
  ], [activeTab, isParent, resolveAssignmentStudentLabel]);

  /* ---------- Handlers ---------- */
  const handleStudentSelect = params => {
    const student = students.find(s => s.id === params.id);
    setSelectedStudent(student);
    fetchAssignments(student);
  };

  const handleDialogClose = (changed=false) => {
    setDialogOpen(false);
    if (changed) {
      if (selectedStudent) fetchAssignments(selectedStudent);
      else fetchAllAssignments();
    }
  };

  const handleViewQuestions = async (row) => {
    try {
      const { test: apiTest, questions } = await testService.loadTestQuestionsForView(row.test);
      const test = {
        ...row,
        ...apiTest,
        title: apiTest.title || row.title
      };
      setQuestionsDialog({ open: true, questions, test });
    } catch (e) {
      showMsg(e?.message || 'Failed to load test questions', 'error');
    }
  };

  const handleViewResult = (row) => {
    if(!row.attemptId) return;
    setResultDialog({open:true,attemptId:row.attemptId});
  };

  const handleMarkManagedTestComplete = async (row) => {
    const msId = row?.teacher_managed_student_id;
    if (!msId || !row?.test || row.status !== 'Pending') return;
    try {
      const attempt = await testService.startManagedAttempt(row.test, msId, 'EXAM');
      try {
        await testService.completeManagedAttempt(attempt.id);
      } catch (completeErr) {
        const detail = completeErr?.response?.data?.detail;
        const already =
          completeErr?.response?.status === 400 &&
          typeof detail === 'string' &&
          detail.toLowerCase().includes('already');
        if (already) {
          showMsg('This test was already marked complete. Refreshing the list.', 'info');
          if (selectedStudent && selectedStudent.id !== 'all') fetchAssignments(selectedStudent);
          else fetchAllAssignments();
          return;
        }
        throw completeErr;
      }
      showMsg('Test marked as completed');
      if (selectedStudent && selectedStudent.id !== 'all') fetchAssignments(selectedStudent);
      else fetchAllAssignments();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Could not mark test complete';
      showMsg(msg, 'error');
    }
  };

  const openAnnotateDialog = async (row) => {
    const sid = row.student || row.student_id || getPlatformStudentPk(selectedStudent) || selectedStudent?.user_id;
    if (!sid || !row.test) return;
    try {
      const uploads = await uploadService.listUploads({ studentId: sid, testId: row.test, role: 'student' });
      const pdfUpload = Array.isArray(uploads) ? uploads.find(u => (u.filename || '').toLowerCase().endsWith('.pdf')) : null;
      if (pdfUpload) {
        setAnnotateDialog({ open: true, uploadId: pdfUpload.id, filename: pdfUpload.filename });
      } else {
        showMsg('No PDF uploaded to annotate. Student can upload a PDF worksheet.', 'warning');
      }
    } catch (e) {
      showMsg(e?.message || 'Failed to load uploads', 'error');
    }
  };

  const handleUnassign = async (row) => {
    if (row.status === 'Completed') return;
    if (!window.confirm('Unassign this test from the student?')) return;
    try {
      await api.delete(`${ASSIGNMENTS}${row.id}/`);
      if (selectedStudent && selectedStudent.id !== 'all') {
        fetchAssignments(selectedStudent);
      } else {
        fetchAllAssignments();
      }
      showMsg('Assignment removed');
    } catch (e) { showMsg(e.message, 'error'); }
  };

  /* ---------- Render ---------- */
  const FilterPanel = ({ variant = 'side' }) => {
    const isTop = variant === 'top';

    return (
    <Paper 
      elevation={2} 
      sx={teacherFilterPanelSx(isTop, 0)}
    >
      <Box sx={teacherFilterHeaderSx(isTop)}>
        <Typography
          variant={isTop ? 'caption' : 'h6'}
          sx={teacherFilterTitleSx(isTop)}
        >
          {isTop ? 'Assignment filters' : 'Filters'}
        </Typography>
        <Button
          size="small"
          startIcon={<Clear />}
          onClick={clearFilters}
          disabled={!search && !selectedStudent}
          sx={teacherFilterClearButtonSx}
        >
          Clear All
        </Button>
      </Box>
      
      <Stack
        spacing={isTop ? 1.5 : 3}
        direction={isTop ? 'row' : 'column'}
        useFlexGap
        flexWrap={isTop ? 'wrap' : 'nowrap'}
        alignItems={isTop ? 'center' : 'stretch'}
        sx={teacherFilterTopStackSx(isTop ? { md: 280, lg: 340 } : '100%')}
      >
        <Autocomplete
          options={[{ id: 'all', email: 'All Students', name: 'All Students' }, ...students]}
          filterOptions={studentFilterOptions}
          getOptionLabel={(option) =>
            (option.email || option.display_name || option.name || '').trim() || 'Student'
          }
          value={selectedStudent}
          loading={studentsLoading}
          onChange={(e, val) => {
            setSelectedStudent(val);
            if (val && val.id === 'all') {
              fetchAllAssignments();
            } else {
              fetchAssignments(val);
            }
          }}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false;
            return String(option.id || '') === String(value.id || '');
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Assigned to" 
              size="small" 
              placeholder={isParent ? "Select child" : "Select student"}
              sx={teacherFilterFieldSx}
            />
          )}
        />
      </Stack>
    </Paper>
    );
  };

  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      textAlign: 'center'
    }}>
      <Box sx={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <AssignmentTurnedInIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
        No assignments found
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Try selecting a different {isParent ? 'child' : 'student'} or adjusting your filters.
      </Typography>
    </Box>
  );

  return (
    <>
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => { if (actionsMenuRow) handleViewQuestions(actionsMenuRow); closeActionsMenu(); }}>
          <ListItemIcon><VisibilityIcon fontSize="small"/></ListItemIcon>
          <ListItemText>View questions</ListItemText>
        </MenuItem>
        <Tooltip
          title={
            actionsMenuRow?.status === 'Pending'
              ? 'Change the completion date for this assignment'
              : 'Due date can only be changed while the test is pending'
          }
        >
          <span style={{ display: 'block' }}>
            <MenuItem
              disabled={!actionsMenuRow || actionsMenuRow.status !== 'Pending'}
              onClick={() => {
                if (actionsMenuRow?.status === 'Pending') openDueDateDialog(actionsMenuRow);
                closeActionsMenu();
              }}
            >
              <ListItemIcon><EventIcon fontSize="small"/></ListItemIcon>
              <ListItemText>Edit due date</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={
            actionsMenuRow?.status === 'Completed'
              ? 'Test already completed'
              : 'Record that the test was taken offline; then upload the worksheet from the Test Taken tab.'
          }
        >
          <span style={{ display: 'block' }}>
            <MenuItem
              disabled={
                !actionsMenuRow ||
                actionsMenuRow.status === 'Completed' ||
                !actionsMenuRow.teacher_managed_student_id
              }
              onClick={() => {
                if (actionsMenuRow?.status === 'Pending' && actionsMenuRow.teacher_managed_student_id) {
                  handleMarkManagedTestComplete(actionsMenuRow);
                }
                closeActionsMenu();
              }}
            >
              <ListItemIcon><CheckCircleOutlineIcon fontSize="small"/></ListItemIcon>
              <ListItemText>Mark test as completed</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={
          !actionsMenuRow || actionsMenuRow.status !== 'Completed'
            ? 'Result available after completion'
            : !hasWorksheetUploadedAssignment(actionsMenuRow)
              ? 'Student must upload worksheet before grading'
              : 'Start grading'
        }>
          <span style={{ display: 'block' }}>
            <MenuItem
              disabled={!actionsMenuRow || actionsMenuRow.status !== 'Completed' || !hasWorksheetUploadedAssignment(actionsMenuRow)}
              onClick={() => { if (actionsMenuRow?.status === 'Completed' && hasWorksheetUploadedAssignment(actionsMenuRow)) handleViewResult(actionsMenuRow); closeActionsMenu(); }}
            >
              <ListItemIcon><AssignmentTurnedInIcon fontSize="small"/></ListItemIcon>
              <ListItemText>Start grading</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={actionsMenuRow?.status === 'Completed' ? 'Test already completed' : 'Unassign'}>
          <span style={{ display: 'block' }}>
            <MenuItem
              disabled={!actionsMenuRow || actionsMenuRow.status === 'Completed'}
              onClick={() => { if (actionsMenuRow?.status !== 'Completed') handleUnassign(actionsMenuRow); closeActionsMenu(); }}
            >
              <ListItemIcon><DeleteOutlineIcon fontSize="small"/></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <FilterPanel />
        </Box>
      </Drawer>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflowX: 'auto', minWidth: 0 }}>
        {/* Desktop Layout – align filter card to top of main content */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <FilterPanel variant="top" />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 3 
              }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                    {isParent ? 'Child Assignments' : 'Student Assignments'}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
                      {assignments.length}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      total assignment{assignments.length === 1 ? '' : 's'}
                    </Typography>
                  </Stack>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<AssignmentTurnedInIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setAssignMenuAnchor(e.currentTarget)}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-1px)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  Assign Test
                </Button>
                <Menu
                  anchorEl={assignMenuAnchor}
                  open={Boolean(assignMenuAnchor)}
                  onClose={() => setAssignMenuAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setAssignMenuAnchor(null);
                      setDialogOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <RefreshIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Pre Configured Test</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAssignMenuAnchor(null);
                      setRevisionDialogOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <RefreshIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Revision Test</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAssignMenuAnchor(null);
                      setNewTestDialogOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>New Test</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                {TAB_CONFIG.map((tab, idx) => (
                  <Tooltip key={tab.key} title={tab.tooltip} placement="bottom">
                    <Tab label={renderTabLabel(tab, idx)} id={`assign-tab-${idx}`} />
                  </Tooltip>
                ))}
              </Tabs>

              {/* Content */}
              {assignLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : filteredAssignments.length === 0 ? (
                <EmptyState />
              ) : (
                <DataGrid
                  autoHeight
                  rows={filteredAssignments}
                  columns={assignmentColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  sx={{
                    border: 'none',
                    minWidth: 900,
                    '& .MuiDataGrid-row': {
                      '&:nth-of-type(even)': {
                        backgroundColor: 'grey.50'
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.paper',
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    },
                    '& .MuiDataGrid-columnHeader': {
                      fontWeight: 600
                    }
                  }}
                />
              )}
            </Paper>
          </Box>
        </Box>

        {/* Mobile Layout — same design pattern: summary card, horizontal actions, tabs */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, minWidth: 0 }}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              borderRadius: 3,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <SummaryCard
              label="Total Assignments"
              value={assignments.length}
              icon={AssignmentTurnedInIcon}
            />

            {/* Horizontally scrollable action bar */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1,
                mb: 2,
                flexShrink: 0,
                '&::-webkit-scrollbar': { height: 6 },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFilterOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Filters
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AssignmentTurnedInIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setAssignMenuAnchor(e.currentTarget)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Assign Test
              </Button>
            </Box>

            {/* Mobile Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minHeight: 48 }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {TAB_CONFIG.map((tab, idx) => (
                <Tooltip key={tab.key} title={tab.tooltip} placement="bottom">
                  <Tab label={renderTabLabel(tab, idx)} id={`assign-tab-mobile-${idx}`} />
                </Tooltip>
              ))}
            </Tabs>


            {/* Mobile Content – card list (no horizontal scroll) */}
            {assignLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredAssignments.length === 0 ? (
              <EmptyState />
            ) : (
              <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                {filteredAssignments.map((row) => {
                  const currentTabKey = TAB_CONFIG[activeTab]?.key;
                  let displayStatus = row.status;
                  if (currentTabKey === 'ready_for_review' && row.status === 'Completed') displayStatus = 'Worksheet uploaded';
                  else if (currentTabKey === 'grading_complete' && row.gradingComplete) displayStatus = 'Grading complete';
                  const studentName = resolveAssignmentStudentLabel(row);
                  const topicStr = Array.isArray(row.topics_list) ? row.topics_list.join(', ') : (row.topic || '');
                  return (
                    <Paper
                      key={row.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={row.title}>
                          {row.title || '—'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          <Chip size="small" label={row.subject || '—'} sx={{ fontSize: '0.75rem' }} />
                          <Chip
                            size="small"
                            variant="outlined"
                            color={row.assignment_origin_label === 'Teacher-managed' ? 'warning' : 'info'}
                            label={row.assignment_origin_label || 'Linked student'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                          {topicStr ? (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '60%' }} title={topicStr}>
                              {topicStr}
                            </Typography>
                          ) : null}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {isParent ? 'Child' : 'Student'}: {studentName}
                            {row.year ? ` · Year ${row.year}` : ''}
                            {row.target_date ? ` · Due ${formatDueDate(row.target_date)}` : ''}
                          </Typography>
                          <Chip
                            size="small"
                            label={displayStatus}
                            color={row.status === 'Completed' || displayStatus === 'Grading complete' ? 'success' : 'warning'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          endIcon={<ArrowDropDownIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionsMenuAnchor(e.currentTarget);
                            setActionsMenuRow(row);
                          }}
                          sx={{ textTransform: 'none', mt: 0.5 }}
                        >
                          Actions
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>
      </Box>

        {/* Assign dialog - Pre Configured Test */}
        <AssignTestsDialog
          open={dialogOpen}
          student={selectedStudent}
          excludeIds={assignments.map(a => a.test)}
          onClose={handleDialogClose}
        />

        {/* Revision Test Dialog */}
        <AutoTestDialog
          open={revisionDialogOpen}
          onClose={() => setRevisionDialogOpen(false)}
          onSave={async (revisionTestData) => {
            // Get selected student from dialog or fall back to filter panel selection
            const studentToAssign = revisionTestData.selectedStudent || selectedStudent;

            if (!studentToAssign) {
              return { error: 'Please select a student' };
            }
            if (isManagedRosterStudent(studentToAssign)) {
              return {
                error:
                  'Revision tests require an onboarded student account. Use "Pre Configured Test" for roster-only students.',
              };
            }

            try {
              const getStudentPk = s => {
                if (!s) return null;
                const possibleId = (s.student && typeof s.student === 'number' ? s.student : null) ??
                                  (s.student && typeof s.student === 'object' && s.student.id) ??
                                  (s.user && s.user.id) ??
                                  s.user_id ??
                                  s.student_id ??
                                  (s.id && s.id !== false ? s.id : null) ??
                                  s.pk;
                return possibleId;
              };
              
              // Extract selectedStudent from testData before sending to API
              const { selectedStudent: _, ...testPayload } = revisionTestData;
              
              const studentId = getStudentPk(studentToAssign);
              
              if (!studentId) {
                throw new Error('Could not extract valid student ID');
              }
              
              // Create test with target_student_id for question selection, but don't auto-assign
              // We'll assign after reviewing questions
              const payload = {
                ...testPayload,
                test_type: TEST_TYPES.REVISE,
                my_questions: false,
                target_student_id: studentId, // Used for question selection, but won't auto-assign
                // Don't set auto_assign_to_student - we'll assign after review
              };
              
              console.log('Creating revision test with payload:', payload);
              const response = await api.post(TESTS_GENERATE, payload);

              if (response.data && response.data.id) {
                const testId = response.data.id;
                
                // Fetch questions for the test
                try {
                  const questionsResponse = await api.get(`${TESTS}${testId}/questions/`);
                  const questions = Array.isArray(questionsResponse.data) 
                    ? questionsResponse.data 
                    : (questionsResponse.data.results || []);
                  
                  // Store data for review dialog
                  setGeneratedTestId(testId);
                  setGeneratedQuestions(questions);
                  setPendingTestData({
                    ...revisionTestData,
                    studentId: getStudentPk(studentToAssign),
                    assignmentInstructions: 'Revision test focusing on questions you answered incorrectly in the past.'
                  });
                  setPendingStudent(studentToAssign);
                  
                  // Close creation dialog and open review dialog
                  setRevisionDialogOpen(false);
                  setReviewDialogOpen(true);
                  
                  return { success: true };
                } catch (err) {
                  console.error('Error fetching questions:', err);
                  try {
                    await api.delete(`${TESTS}${testId}/`);
                  } catch (cleanupError) {
                    console.error('Failed to remove unreviewed revision test', cleanupError);
                  }
                  return { error: 'Could not load generated questions for review. The test was not assigned.' };
                }
              }
              
              return { success: false };
            } catch (error) {
              let errorMessage = 'Error creating revision test';
              
              if (error.response && error.response.data) {
                if (typeof error.response.data === 'object' && !error.response.data.detail) {
                  const fieldErrors = Object.entries(error.response.data)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                  errorMessage = fieldErrors || error.response.data.message || error.response.data.error || errorMessage;
                } else {
                  errorMessage = error.response.data.detail || error.response.data.message || error.response.data.error || errorMessage;
                }
              } else if (error.message) {
                errorMessage = error.message;
              }
              
              return { error: errorMessage };
            }
          }}
          subjects={subjects}
          years={years}
          boards={boards}
          topics={topics}
          students={students}
          studentsLoading={studentsLoading}
          initialForm={(() => {
            const form = {
              title:
                selectedStudent && selectedStudent.id !== 'all'
                  ? `Revision Test for ${
                      selectedStudent.email ||
                      selectedStudent.display_name ||
                      selectedStudent.name ||
                      'Student'
                    }`
                  : 'Revision Test',
              subject: subjects.length > 0 ? subjects[0].id : '',
              topics: [],
              test_type: TEST_TYPES.REVISE,
              board: boards.length > 0 ? boards[0].id : '',
              academic_year: years.length > 0 ? years[0].id : '',
              num_questions: 10,
              duration_minutes: 60,
              percent_basic: 50,
              percent_advanced: 50,
              note: 'Please select topics for the revision test. This test will focus on questions the student has answered incorrectly in the past.',
            };
            return form;
          })()}
          showMyQuestions={true}
          title="Create Revision Test"
          saveButtonText="Create Revision Test"
          user={
            selectedStudent && selectedStudent.id !== 'all' && !isManagedRosterStudent(selectedStudent)
              ? selectedStudent
              : null
          }
          isFromAssignment={true}
          initialSelectedStudents={
            selectedStudent && selectedStudent.id !== 'all' ? [selectedStudent] : []
          }
        />

        {/* New Test Dialog with Student Selection */}
        <AutoTestDialog
          open={newTestDialogOpen}
          onClose={() => setNewTestDialogOpen(false)}
          onSave={async (testData) => {
            // Get selected student from dialog or fall back to filter panel selection
            const studentToAssign = testData.selectedStudent || selectedStudent;

            if (!studentToAssign) {
              return { error: 'Please select a student' };
            }

            try {
              // Extract selectedStudent from testData before sending to API
              const { selectedStudent: _, ...testPayload } = testData;
              
              // Create the test (without assigning yet)
              const createResponse = await api.post(TESTS_GENERATE, {
                ...testPayload,
                my_questions: testPayload.my_questions || false,
              });

              if (createResponse.data && createResponse.data.id) {
                const testId = createResponse.data.id;
                
                // Fetch questions for the test
                try {
                  const questionsResponse = await api.get(`${TESTS}${testId}/questions/`);
                  const questions = Array.isArray(questionsResponse.data) 
                    ? questionsResponse.data 
                    : (questionsResponse.data.results || []);
                  
                  // Store data for review dialog
                  setGeneratedTestId(testId);
                  setGeneratedQuestions(questions);
                  setPendingTestData({
                    ...testData,
                    studentId: getStudentPk(studentToAssign)
                  });
                  setPendingStudent(studentToAssign);
                  
                  // Close creation dialog and open review dialog
                  setNewTestDialogOpen(false);
                  setReviewDialogOpen(true);
                  
                  return { success: true };
                } catch (err) {
                  console.error('Error fetching questions:', err);
                  try {
                    await api.delete(`${TESTS}${testId}/`);
                  } catch (cleanupError) {
                    console.error('Failed to remove unreviewed generated test', cleanupError);
                  }
                  return { error: 'Could not load generated questions for review. The test was not assigned.' };
                }
              }
              return { success: false };
            } catch (error) {
              let errorMessage = 'Error creating and assigning test';
              
              if (error.response && error.response.data) {
                if (typeof error.response.data === 'object' && !error.response.data.detail) {
                  const fieldErrors = Object.entries(error.response.data)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                  errorMessage = fieldErrors || error.response.data.message || error.response.data.error || errorMessage;
                } else {
                  errorMessage = error.response.data.detail || error.response.data.message || error.response.data.error || errorMessage;
                }
              } else if (error.message) {
                errorMessage = error.message;
              }
              
              return { error: errorMessage };
            }
          }}
          subjects={subjects}
          years={years}
          boards={boards}
          topics={topics}
          students={students}
          initialForm={{
            title: '',
            subject: '',
            topics: [],
            test_type: TEST_TYPES.QUIZ,
            board: '',
            academic_year: '',
            num_questions: 10,
            duration_minutes: 60,
            percent_basic: 50,
            percent_advanced: 50,
            note: '',
          }}
          showMyQuestions={true}
          title="Create New Test"
          saveButtonText="Create and Assign"
          isFromAssignment={true}
          studentsLoading={studentsLoading}
        />

        <ViewTestQuestionsDialog
          open={questionsDialog.open}
          onClose={() => setQuestionsDialog({ open: false, questions: [], test: null })}
          test={questionsDialog.test}
          questions={questionsDialog.questions}
          onMessage={(msg, severity) => showMsg(msg, severity)}
        />

        {/* Result dialog – refresh assignments on close so grading-complete moves without manual refresh */}
        <Dialog
          open={resultDialog.open}
          onClose={() => {
            setResultDialog({ open: false, attemptId: null });
            if (selectedStudent) fetchAssignments(selectedStudent);
            else fetchAllAssignments();
          }}
          fullScreen
        >
          <DialogContent sx={{p:0}}>
            {resultDialog.attemptId && (
              <TestResultsPage
                attemptId={resultDialog.attemptId}
                onBack={() => {
                  setResultDialog({ open: false, attemptId: null });
                  if (selectedStudent) fetchAssignments(selectedStudent);
                  else fetchAllAssignments();
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* PDF Annotation modal */}
        <PdfAnnotationViewer
          open={annotateDialog.open}
          onClose={()=>setAnnotateDialog({ open: false, uploadId: null, filename: null })}
          uploadId={annotateDialog.uploadId}
          filename={annotateDialog.filename}
          role="teacher"
        />

        {/* Question Review Dialog */}
        <TestQuestionReviewDialog
          open={reviewDialogOpen}
          onClose={async () => {
            const testId = generatedTestId;
            setReviewDialogOpen(false);
            setGeneratedTestId(null);
            setGeneratedQuestions([]);
            setPendingTestData(null);
            setPendingStudent(null);
            if (testId) {
              try {
                await api.delete(`${TESTS}${testId}/`);
              } catch (cleanupError) {
                console.error('Failed to discard unreviewed generated test', cleanupError);
                showMsg('Could not discard the unreviewed draft test. Please remove it from the test list.', 'error');
              }
            }
          }}
          testId={generatedTestId}
          questions={generatedQuestions}
          testData={pendingTestData}
          studentToAssign={pendingStudent}
          onAssign={async (testId, updatedQuestions, testData, studentToAssign, targetDate) => {
            const msId = getManagedStudentRecordId(studentToAssign);
            const platformId = getPlatformStudentPk(studentToAssign);
            if (!msId && !platformId) {
              throw new Error('Could not resolve student for assignment');
            }

            if (!targetDate) {
              throw new Error('Completion date is required');
            }
            
            try {
              // First, ensure the test is published (required for assignment)
              try {
                await api.patch(`${TESTS}${testId}/`, {
                  status: 'PUBLISHED'
                });
              } catch (publishError) {
                console.warn('Could not publish test (may already be published):', publishError);
                // Continue anyway - test might already be published
              }
              
              await api.post(
                ASSIGNMENTS,
                buildAssignmentPayloadFor(studentToAssign, testId, {
                  target_date: targetDate,
                  assignment_instructions: testData?.assignmentInstructions || testData?.note || '',
                })
              );
              
              showMsg('Test created and assigned successfully!');
            } catch (assignError) {
              console.error('Error assigning test:', assignError);
              const errorMsg = assignError.response?.data?.detail || 
                              assignError.response?.data?.error || 
                              assignError.message || 
                              'Failed to assign test';
              throw new Error(errorMsg);
            }
            
            setReviewDialogOpen(false);
            setGeneratedTestId(null);
            setGeneratedQuestions([]);
            setPendingTestData(null);
            setPendingStudent(null);
            
            // Refresh assignments - always refresh to ensure new assignment appears
            // Use a small delay to ensure backend has processed the assignment
            setTimeout(() => {
              // If a student is selected, try to refresh their assignments
              // Otherwise, refresh all assignments
              if (selectedStudent) {
                const same =
                  selectedStudent &&
                  studentToAssign &&
                  String(selectedStudent.id) === String(studentToAssign.id);
                if (same) {
                  fetchAssignments(selectedStudent);
                } else {
                  fetchAllAssignments();
                }
              } else {
                fetchAllAssignments();
              }
            }, 500);
          }}
        />

        {/* Edit due date dialog (pending assignments only) */}
        <Dialog
          open={dueDateDialog.open}
          onClose={() => { if (!dueDateSaving) setDueDateDialog({ open: false, row: null }); }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Edit due date</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {dueDateDialog.row?.title || 'Assignment'}
              {dueDateDialog.row ? ` — ${resolveAssignmentStudentLabel(dueDateDialog.row)}` : ''}
            </Typography>
            <TextField
              fullWidth
              label="Completion Date *"
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              sx={{ mt: 1 }}
              helperText={newDueDate ? `Selected: ${formatDueDate(newDueDate)}` : 'Select the new completion date'}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDueDateDialog({ open: false, row: null })} disabled={dueDateSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveDueDate} disabled={dueDateSaving || !newDueDate}>
              {dueDateSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </>
  );
}

export default StudentAssignmentsPage;
